import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_URL, NOTIFICATION_POLL_MS } from './constants';
import { toAppUser } from './helpers';
import type { AppLayoutData, AppNotification } from './types';

type UnreadCounts = { unread_notifications: number; unread_inbox: number };

async function fetchLayoutData(): Promise<AppLayoutData> {
  const token = localStorage.getItem('token') || '';
  const headers = { Authorization: 'Bearer ' + token };

  const [userRes, countsRes] = await Promise.all([
    axios.get(API_URL + '/me/', { headers }),
    axios.get<UnreadCounts>(API_URL + '/notifications/unread-counts/', { headers })
      .catch(() => ({ data: { unread_notifications: 0, unread_inbox: 0 } })),
  ]);

  const user = toAppUser(userRes.data);
  if (!user) {
    throw new Error('Missing user in /me response');
  }

  return {
    user,
    unreadNotifications: countsRes.data.unread_notifications ?? 0,
    unreadInbox: countsRes.data.unread_inbox ?? 0,
  };
}

export function useAppLayoutData() {
  return useQuery({
    queryKey: ['app-layout-data'],
    queryFn: fetchLayoutData,
    refetchInterval: NOTIFICATION_POLL_MS,
    staleTime: 30_000,
    retry: 1,
  });
}

async function fetchNotifications(limit = 20): Promise<AppNotification[]> {
  const token = localStorage.getItem('token') || '';
  const res = await axios.get<AppNotification[]>(
    API_URL + '/notifications/?limit=' + limit,
    { headers: { Authorization: 'Bearer ' + token } }
  );
  return res.data ?? [];
}

export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications-list'],
    queryFn: () => fetchNotifications(20),
    enabled,
    staleTime: 15_000,
  });
}
